/**
 * Soccer Position Recommendation Rules
 * 
 * Defines position recommendations for Soccer players based on their attributes
 */

import type { PositionRule } from '../positionRecommendations';

/**
 * Soccer Position Rules
 */
export const SOCCER_POSITION_RULES: PositionRule[] = [
  // PREMIUM POSITIONS - Require elevated thresholds
  {
    position: 'GK',
    tier: 'premium',
    description: 'Goalkeeper - Requires positioning, reflexes, and distribution',
    requirements: [
      { attribute: 'positioning', minValue: 7, required: true },
      { attribute: 'awareness', minValue: 7, required: true },
      { attribute: 'agility', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const positioning = attrs.get('positioning') || 0;
      const awareness = attrs.get('awareness') || 0;
      const agility = attrs.get('agility') || 0;
      const speed = attrs.get('speed') || 0;
      const passing = attrs.get('passing') || 0;
      return positioning * 2.0 + awareness * 1.8 + agility * 1.2 + speed * 1.0 + passing * 0.8;
    },
  },
  {
    position: 'CAM',
    tier: 'premium',
    description: 'Central Attacking Midfielder - Requires creativity, passing, and vision',
    requirements: [
      { attribute: 'passing', minValue: 7, required: true },
      { attribute: 'dribbling', minValue: 7, required: true },
      { attribute: 'awareness', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const passing = attrs.get('passing') || 0;
      const dribbling = attrs.get('dribbling') || 0;
      const awareness = attrs.get('awareness') || 0;
      const shooting = attrs.get('shooting') || 0;
      const sport_iq = attrs.get('sport_iq') || 0;
      return passing * 1.8 + dribbling * 1.6 + awareness * 1.4 + shooting * 1.0 + sport_iq * 1.2;
    },
  },

  // STANDARD POSITIONS - Moderate thresholds
  {
    position: 'ST',
    tier: 'standard',
    description: 'Striker - Requires finishing, positioning, and movement',
    requirements: [
      { attribute: 'shooting', minValue: 7, required: true },
      { attribute: 'positioning', minValue: 6, required: true },
      { attribute: 'speed', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const shooting = attrs.get('shooting') || 0;
      const positioning = attrs.get('positioning') || 0;
      const speed = attrs.get('speed') || 0;
      const agility = attrs.get('agility') || 0;
      const dribbling = attrs.get('dribbling') || 0;
      return shooting * 2.0 + positioning * 1.5 + (speed + agility) * 1.2 + dribbling * 1.0;
    },
  },
  {
    position: 'CF',
    tier: 'standard',
    description: 'Center Forward - Versatile forward, needs link-up play and finishing',
    requirements: [
      { attribute: 'shooting', minValue: 6, required: true },
      { attribute: 'passing', minValue: 6, required: true },
      { attribute: 'positioning', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const shooting = attrs.get('shooting') || 0;
      const passing = attrs.get('passing') || 0;
      const positioning = attrs.get('positioning') || 0;
      const dribbling = attrs.get('dribbling') || 0;
      const awareness = attrs.get('awareness') || 0;
      return shooting * 1.6 + passing * 1.4 + positioning * 1.3 + dribbling * 1.1 + awareness * 1.1;
    },
  },
  {
    position: 'LW',
    tier: 'standard',
    description: 'Left Wing - Requires pace, dribbling, and crossing',
    requirements: [
      { attribute: 'dribbling', minValue: 6, required: true },
      { attribute: 'speed', minValue: 6, required: true },
      { attribute: 'passing', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const dribbling = attrs.get('dribbling') || 0;
      const speed = attrs.get('speed') || 0;
      const passing = attrs.get('passing') || 0;
      const agility = attrs.get('agility') || 0;
      const shooting = attrs.get('shooting') || 0;
      return dribbling * 1.6 + speed * 1.5 + passing * 1.2 + agility * 1.1 + shooting * 0.9;
    },
  },
  {
    position: 'RW',
    tier: 'standard',
    description: 'Right Wing - Requires pace, dribbling, and crossing',
    requirements: [
      { attribute: 'dribbling', minValue: 6, required: true },
      { attribute: 'speed', minValue: 6, required: true },
      { attribute: 'passing', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const dribbling = attrs.get('dribbling') || 0;
      const speed = attrs.get('speed') || 0;
      const passing = attrs.get('passing') || 0;
      const agility = attrs.get('agility') || 0;
      const shooting = attrs.get('shooting') || 0;
      return dribbling * 1.6 + speed * 1.5 + passing * 1.2 + agility * 1.1 + shooting * 0.9;
    },
  },
  {
    position: 'CB',
    tier: 'standard',
    description: 'Center Back - Requires defensive positioning, tackling, and aerial ability',
    requirements: [
      { attribute: 'tackling', minValue: 6, required: true },
      { attribute: 'positioning', minValue: 6, required: true },
      { attribute: 'awareness', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const tackling = attrs.get('tackling') || 0;
      const positioning = attrs.get('positioning') || 0;
      const awareness = attrs.get('awareness') || 0;
      const strength = attrs.get('strength') || 0;
      const passing = attrs.get('passing') || 0;
      return tackling * 1.6 + positioning * 1.5 + awareness * 1.4 + strength * 1.2 + passing * 0.9;
    },
  },
  {
    position: 'CDM',
    tier: 'standard',
    description: 'Central Defensive Midfielder - Requires defensive awareness and distribution',
    requirements: [
      { attribute: 'tackling', minValue: 6, required: true },
      { attribute: 'passing', minValue: 6, required: true },
      { attribute: 'positioning', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const tackling = attrs.get('tackling') || 0;
      const passing = attrs.get('passing') || 0;
      const positioning = attrs.get('positioning') || 0;
      const awareness = attrs.get('awareness') || 0;
      const stamina = attrs.get('stamina') || 0;
      return tackling * 1.5 + passing * 1.5 + positioning * 1.3 + awareness * 1.2 + stamina * 1.0;
    },
  },

  // EASIER POSITIONS - Lower thresholds (accessible for less developed players)
  {
    position: 'LB',
    tier: 'easier',
    description: 'Left Back - Requires defensive positioning and some pace',
    requirements: [
      { attribute: 'positioning', minValue: 5, required: true },
      { attribute: 'speed', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const positioning = attrs.get('positioning') || 0;
      const speed = attrs.get('speed') || 0;
      const tackling = attrs.get('tackling') || 0;
      const passing = attrs.get('passing') || 0;
      const stamina = attrs.get('stamina') || 0;
      return positioning * 1.6 + speed * 1.3 + tackling * 1.2 + passing * 1.0 + stamina * 1.0;
    },
  },
  {
    position: 'RB',
    tier: 'easier',
    description: 'Right Back - Requires defensive positioning and some pace',
    requirements: [
      { attribute: 'positioning', minValue: 5, required: true },
      { attribute: 'speed', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const positioning = attrs.get('positioning') || 0;
      const speed = attrs.get('speed') || 0;
      const tackling = attrs.get('tackling') || 0;
      const passing = attrs.get('passing') || 0;
      const stamina = attrs.get('stamina') || 0;
      return positioning * 1.6 + speed * 1.3 + tackling * 1.2 + passing * 1.0 + stamina * 1.0;
    },
  },
];

